import axios from 'axios';
import { CURR_API } from '@/constants/apis';
import { redirectToCodePage } from '@/api/mfaRequired';
import { REQUEST_MARKER, isOurOwnOrigin } from '@/api/requestMarker';
import {
  DOCUMENT_UNAVAILABLE_TEXT,
  SCAN_PENDING_TEXT,
  isFileUid,
  keyonBundleUrl,
  keyonFileUrl,
} from '@/utils/fileUrl';
import { validateDeleteReason } from '@/utils/fileDelete';

/**
 * Stored files, reached only through keyon.
 *
 * Downloads go through file.php by opaque id; listing, removing and uploading
 * credential documents, and removing a directive document or a message
 * attachment, go through tswebhook.php. This file is the one place the request
 * envelopes for ListCredentialDocuments, DeleteCredentialDocument, AddNewPdf,
 * DeleteDirectiveDocument and DeleteMessageAttachment are spelled, so a change on
 * keyon's side is reconciled here.
 *
 * Every function takes injectable `{ client, doc, urlApi, onMfaRequired }` so it
 * can be tested without a browser, a server or a router.
 */

const WEBHOOK = `${CURR_API}/tswebhook.php`;

function defaults(deps = {}) {
  return {
    client: deps.client || axios,
    doc: deps.doc || (typeof document !== 'undefined' ? document : null),
    urlApi: deps.urlApi || (typeof window !== 'undefined' ? window.URL : null),
    onMfaRequired: deps.onMfaRequired || (() => redirectToCodePage()),
  };
}

/**
 * file.php's status codes as the states a page can show.
 * @returns {'scan_pending'|'unavailable'|'mfa_required'|'rate_limited'|'error'}
 */
export function refusalStateFromResponse(status, code) {
  if (status === 409) return 'scan_pending';
  if (status === 404 || status === 410) return 'unavailable';
  if (status === 401) return code === 'mfa_required' ? 'mfa_required' : 'unavailable';
  if (status === 429) return 'rate_limited';
  return 'error';
}

/**
 * The sentence a page shows for a failed download. `mfa_required` has none:
 * the download has already sent the user back to the code page.
 * @returns {string|null}
 */
export function downloadRefusalText(state) {
  if (state === 'mfa_required') return null;
  if (state === 'scan_pending') return SCAN_PENDING_TEXT;
  if (state === 'unavailable') return DOCUMENT_UNAVAILABLE_TEXT;
  if (state === 'rate_limited') return 'Too many downloads right now. Please wait a moment and try again.';
  return 'Unable to download the file. Please try again.';
}

// jsdom's Blob (and older Safari's) has no text(); FileReader works everywhere.
function readBlobText(blob) {
  if (blob && typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

async function refusalCode(data) {
  try {
    if (data && typeof data === 'object' && !(typeof Blob !== 'undefined' && data instanceof Blob)) {
      return typeof data.code === 'string' ? data.code : null;
    }
    const parsed = JSON.parse(await readBlobText(data));
    return parsed && typeof parsed.code === 'string' ? parsed.code : null;
  } catch (_) {
    return null;
  }
}

/*
 * Why a blob fetch and not downloadjs or a plain link:
 * - downloadjs fetches with its own XHR and saves whatever comes back, so a
 *   409/410 JSON error body would be saved as the "document"
 *   (node_modules/downloadjs/download.js:51-57), and it never reports the status.
 * - A plain <a href download> gives the page no status either, so a 409 could
 *   never become the "Scanning for malware" state.
 * One GET also means exactly one FileDownload audit row per click. The object
 * URL is a blob: URL of our own origin holding bytes keyon already streamed; it
 * is not a storage URL, and it is revoked after a second (Safari needs the delay
 * before it has started the save).
 */
async function saveFromUrl(url, filename, deps) {
  const { client, doc, urlApi, onMfaRequired } = defaults(deps);
  let res;
  try {
    res = await client.get(url, { responseType: 'blob', validateStatus: () => true });
  } catch (_) {
    return { ok: false, state: 'error' };
  }
  if (!res || typeof res.status !== 'number') return { ok: false, state: 'error' };
  if (res.status < 200 || res.status >= 300) {
    const state = refusalStateFromResponse(res.status, await refusalCode(res.data));
    // validateStatus lets every status through, so the axios interceptor never
    // sees this 401; the redirect it would have made happens here instead.
    if (state === 'mfa_required') onMfaRequired();
    return { ok: false, state };
  }
  const objectUrl = urlApi.createObjectURL(res.data);
  const a = doc.createElement('a');
  a.href = objectUrl;
  a.download = filename || 'download';
  a.rel = 'noopener';
  if (doc.body && typeof doc.body.appendChild === 'function') doc.body.appendChild(a);
  a.click();
  if (typeof a.remove === 'function') a.remove();
  setTimeout(() => urlApi.revokeObjectURL(objectUrl), 1000);
  return { ok: true };
}

/**
 * Save one stored file. `other` files are always served as attachments by
 * keyon whatever is asked; this always asks for download.
 * @returns {Promise<{ok:true}|{ok:false,state:'scan_pending'|'unavailable'|'mfa_required'|'rate_limited'|'error'}>}
 */
export async function downloadKeyonFile(uid, filename, deps) {
  const url = keyonFileUrl(uid, { download: true });
  if (!url) return { ok: false, state: 'unavailable' };
  return saveFromUrl(url, filename, deps);
}

/** One owner's credential documents as credentials.zip; same result shape. */
export async function downloadCredentialBundle(ownerUniqueId, deps) {
  const url = keyonBundleUrl(ownerUniqueId);
  if (!url) return { ok: false, state: 'unavailable' };
  return saveFromUrl(url, 'credentials.zip', deps);
}

function refusalText(data) {
  if (!data || typeof data !== 'object') return null;
  const text = data.message || data.error;
  return typeof text === 'string' && text.trim() !== '' ? text : null;
}

// axios rejects on a 4xx/5xx; keyon's refusal body is still the thing to read.
async function postWebhook(client, body, config) {
  try {
    const res = config ? await client.post(WEBHOOK, body, config) : await client.post(WEBHOOK, body);
    return { data: res && res.data };
  } catch (err) {
    return { data: err && err.response ? err.response.data : null, failed: true };
  }
}

const LIST_SCOPES = ['own', 'org'];

/**
 * @typedef {object} CredentialItem
 * @property {string} file_uid
 * @property {'license'|'insurance'|'misc'|'other'} category
 * @property {string} name
 * @property {string} content_type
 * @property {'image'|'pdf'|'other'} file_class
 * @property {number} byte_size
 * @property {string} created_at  'YYYY-MM-DD HH:MM:SS' (MySQL text, no zone)
 * @property {string} owner_unique_id
 * @property {string} scan_status
 * @property {string} owner_name  copied from the item's owner group
 */

/**
 * `own` for the personal views, `org` only from the aggregated admin view; keyon
 * enforces privilege 60 for `org` either way. keyon groups items by owner; the
 * pages want one list, so the groups are flattened here in keyon's order.
 * @returns {Promise<{ok:true,items:CredentialItem[]}|{ok:false,message:string|null}>}
 */
export async function listCredentialDocuments({ scope = 'own' } = {}, deps) {
  if (!LIST_SCOPES.includes(scope)) return { ok: false, message: null };
  const { client } = defaults(deps);
  const { data } = await postWebhook(client, { PdfList: { API_Meth: 'ListCredentialDocuments', scope } });
  if (!data || data.status !== 'Successful' || !Array.isArray(data.groups)) {
    return { ok: false, message: refusalText(data) };
  }
  const items = [];
  data.groups.forEach((group) => {
    if (!group || !Array.isArray(group.items)) return;
    group.items.forEach((item) => {
      if (item && typeof item === 'object') items.push({ ...item, owner_name: group.owner_name || '' });
    });
  });
  return { ok: true, items };
}

/**
 * Removes the document and clears any license/insurance reference to it.
 * @returns {Promise<{ok:true}|{ok:false,message:string|null}>}
 */
export async function deleteCredentialDocument({ file_uid: fileUid, reason = 'user_removed' } = {}, deps) {
  if (!isFileUid(fileUid)) return { ok: false, message: null };
  const { client } = defaults(deps);
  const { data } = await postWebhook(client, {
    PdfList: { API_Meth: 'DeleteCredentialDocument', file_uid: fileUid, reason },
  });
  if (data && data.status === 'Successful') return { ok: true };
  return { ok: false, message: refusalText(data) };
}

/**
 * Releases a credential file a form stored through UploadNurseFile and never
 * saved: the form was closed or unmounted, or another file took its slot.
 * keyon removes it only while it is still the caller's own unclaimed upload
 * (DeleteCredentialDocument with unclaimed_only), so a saved credential is
 * never touched this way, and the nightly sweep still collects whatever this
 * misses - a closed tab, a request that never arrived.
 *
 * Best-effort: it never throws, shows nothing, and the close it follows does
 * not wait for it. It is sent with fetch rather than axios, for two reasons:
 * `keepalive` lets it finish when the page is going away, and it stays clear of
 * the global axios MFA handler (mfaRequired.js), which would otherwise send a
 * user whose MFA row lapsed to the code page in the middle of a cancel. That
 * also means installRequestMarker never sees it, so the marker is set here.
 * @returns {Promise<boolean>} whether keyon reported the file released
 */
export async function discardUnclaimedCredentialUpload(fileUid, deps = {}) {
  if (!isFileUid(fileUid)) return false;
  const send = deps.fetch || (typeof fetch === 'function' ? (...args) => fetch(...args) : null);
  if (!send) return false;
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (isOurOwnOrigin(WEBHOOK)) headers[REQUEST_MARKER] = '1';
    const res = await send(WEBHOOK, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers,
      body: JSON.stringify({
        PdfList: { API_Meth: 'DeleteCredentialDocument', file_uid: fileUid, unclaimed_only: true },
      }),
    });
    if (!res || !res.ok) return false;
    const data = await res.json();
    return !!data && data.status === 'Successful';
  } catch (_) {
    return false;
  }
}

function deleteRefusal(data) {
  return {
    ok: false,
    message: refusalText(data),
    code: data && typeof data === 'object' && typeof data.code === 'string' ? data.code : null,
  };
}

const NOT_SENT = Object.freeze({ ok: false, message: null, code: null });

/**
 * Removes a pre-signature directive's uploaded document; keyon keeps the record
 * and holds the file for retention. Nothing is sent without a uid, a positive
 * record id and a reason. A refusal carries keyon's sentence and code
 * (reason_required, directive_finalized, forbidden, not_found, already_deleted).
 * @returns {Promise<{ok:true}|{ok:false,message:string|null,code:string|null}>}
 */
export async function deleteDirectiveDocument({ directive_record_id: recordId, file_uid: fileUid, reason } = {}, deps) {
  const id = typeof recordId === 'number' || typeof recordId === 'string' ? Number(recordId) : NaN;
  const checked = validateDeleteReason(reason);
  if (!isFileUid(fileUid) || !Number.isInteger(id) || id <= 0 || !checked.ok) return { ...NOT_SENT };
  const { client } = defaults(deps);
  const { data } = await postWebhook(client, {
    Directives: { API_Meth: 'DeleteDirectiveDocument', directive_record_id: id, file_uid: fileUid, reason: checked.reason },
  });
  if (data && data.status === 'Successful') return { ok: true };
  return deleteRefusal(data);
}

/**
 * Removes an attachment from every message its sender sent it with; only the
 * sender may. Same result shape as deleteDirectiveDocument.
 * @returns {Promise<{ok:true}|{ok:false,message:string|null,code:string|null}>}
 */
export async function deleteMessageAttachment({ file_uid: fileUid, reason } = {}, deps) {
  const checked = validateDeleteReason(reason);
  if (!isFileUid(fileUid) || !checked.ok) return { ...NOT_SENT };
  const { client } = defaults(deps);
  const { data } = await postWebhook(client, {
    Messages: { API_Meth: 'DeleteMessageAttachment', file_uid: fileUid, reason: checked.reason },
  });
  if (data && data.status === 'Successful') return { ok: true };
  return deleteRefusal(data);
}

/**
 * Upload onto the Documents page. `category` is the credential category
 * (license, insurance, misc, other); `latest` makes a license or insurance the
 * one the person's record points at. No Content-Type is set: the browser writes
 * the multipart boundary, and keyon's cross-site guard accepts only JSON or
 * multipart.
 * @returns {Promise<{ok:true,file_uid:string,scan_status:string}|{ok:false,message:string|null}>}
 */
export async function uploadCredentialDocument({ file, category, latest, accountnumber, externid } = {}, deps) {
  const { client } = defaults(deps);
  const fd = new FormData();
  fd.append('API_Meth', 'AddNewPdf');
  fd.append('subid', String(accountnumber == null ? '' : accountnumber));
  fd.append('externid', String(externid == null ? '' : externid));
  fd.append('pdfType', String(category || ''));
  fd.append('latest', latest === true ? 'true' : 'false');
  fd.append('file', file);
  const { data } = await postWebhook(client, fd, {});
  if (data && isFileUid(data.file_uid)) {
    return { ok: true, file_uid: data.file_uid, scan_status: data.scan_status };
  }
  return { ok: false, message: refusalText(data) };
}
